import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { getAiNotesServiceUrl, MeetingAiApiError, streamAssistantChat } from '../../ai-notes/functions';
import { AI_ASSISTANT_HISTORY_MAX, AI_ASSISTANT_INPUT_MAX_LENGTH, AI_ASSISTANT_REVEAL_CHARS_PER_FRAME } from '../constants';
import logger from '../logger';
import { PortalMessages as M } from '../messages';
/**
 * 门户「AI 小助手」问答页：同源 /meeting-ai 流式调用 DeepSeek。
 */
export default function AiAssistantPage({ onBack }) {
    const serviceUrl = useSelector((state) => getAiNotesServiceUrl(state));
    const [draft, setDraft] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [messages, setMessages] = useState([
        {
            content: M.AI_ASSISTANT_WELCOME,
            id: 'welcome',
            role: 'assistant'
        }
    ]);
    const listRef = useRef(null);
    const seqRef = useRef(0);
    const abortRef = useRef(null);
    const bufferRef = useRef('');
    const shownRef = useRef('');
    const pendingIdRef = useRef(null);
    const rafRef = useRef(null);
    const stopReveal = () => {
        if (rafRef.current !== null) {
            window.cancelAnimationFrame(rafRef.current);
            rafRef.current = null;
        }
    };
    const pumpReveal = () => {
        rafRef.current = null;
        const pendingId = pendingIdRef.current;
        const buf = bufferRef.current;
        const shown = shownRef.current;
        if (!pendingId || buf.length <= shown.length) {
            return;
        }
        const next = buf.slice(0, Math.min(buf.length, shown.length + AI_ASSISTANT_REVEAL_CHARS_PER_FRAME));
        shownRef.current = next;
        setMessages(prev => prev.map(item => (item.id === pendingId
            ? {
                ...item,
                content: next,
                pending: true
            }
            : item)));
        if (next.length < buf.length) {
            rafRef.current = window.requestAnimationFrame(pumpReveal);
        }
    };
    const enqueueDelta = (delta) => {
        bufferRef.current += delta;
        if (rafRef.current === null) {
            rafRef.current = window.requestAnimationFrame(pumpReveal);
        }
    };
    const waitRevealCaughtUp = (signal) => new Promise(resolve => {
        const tick = () => {
            if (signal?.aborted || shownRef.current.length >= bufferRef.current.length) {
                resolve();
                return;
            }
            window.requestAnimationFrame(tick);
        };
        tick();
    });
    useEffect(() => () => {
        abortRef.current?.abort();
        stopReveal();
    }, []);
    useEffect(() => {
        const el = listRef.current;
        if (el) {
            el.scrollTop = el.scrollHeight;
        }
    }, [messages, loading]);
    const onSubmit = async (event) => {
        event.preventDefault();
        const question = draft.trim();
        if (!question) {
            setError(M.AI_ASSISTANT_EMPTY);
            return;
        }
        if (loading) {
            return;
        }
        seqRef.current += 1;
        const userId = `u-${seqRef.current}`;
        const pendingId = `a-${seqRef.current}`;
        const userMsg = {
            content: question,
            id: userId,
            role: 'user'
        };
        const history = [...messages, userMsg]
            .filter(item => item.id !== 'welcome' && !item.pending)
            .map(item => ({
            content: item.content,
            role: item.role
        }))
            .slice(-AI_ASSISTANT_HISTORY_MAX);
        abortRef.current?.abort();
        stopReveal();
        const controller = new AbortController();
        abortRef.current = controller;
        bufferRef.current = '';
        shownRef.current = '';
        pendingIdRef.current = pendingId;
        setDraft('');
        setError('');
        setLoading(true);
        setMessages(prev => [
            ...prev,
            userMsg,
            {
                content: '',
                id: pendingId,
                pending: true,
                role: 'assistant'
            }
        ]);
        logger.info(`[AI小助手] ${new Date().toISOString()} 发送流式提问`
            + ` | chars=${question.length} history=${history.length} serviceUrl=${serviceUrl}`);
        try {
            const reply = await streamAssistantChat(serviceUrl, history, enqueueDelta, controller.signal);
            bufferRef.current = reply;
            if (rafRef.current === null && shownRef.current.length < reply.length) {
                rafRef.current = window.requestAnimationFrame(pumpReveal);
            }
            await waitRevealCaughtUp(controller.signal);
            pendingIdRef.current = null;
            setMessages(prev => prev.map(item => (item.id === pendingId
                ? {
                    ...item,
                    content: reply,
                    pending: false
                }
                : item)));
            logger.info(`[AI小助手] ${new Date().toISOString()} 流式回答完成 | replyChars=${reply.length}`);
        }
        catch (err) {
            if (controller.signal.aborted) {
                stopReveal();
                logger.info(`[AI小助手] ${new Date().toISOString()} 流式问答已取消`);
                return;
            }
            const detail = err instanceof MeetingAiApiError
                ? err.message
                : (err instanceof Error ? err.message : String(err));
            setMessages(prev => prev
                .map(item => (item.id === pendingId
                ? {
                    ...item,
                    pending: false
                }
                : item))
                .filter(item => item.id !== pendingId || Boolean(item.content.trim())));
            setError(detail || M.AI_ASSISTANT_FAILED);
            logger.error(`[AI小助手] ${new Date().toISOString()} 问答失败 | error=${detail}`);
        }
        finally {
            if (abortRef.current === controller) {
                abortRef.current = null;
            }
            setLoading(false);
        }
    };
    return (React.createElement("div", { className: 'portal-profile portal-profile-detail-view portal-ai-chat' },
        React.createElement("header", { className: 'portal-profile-detail-header' },
            React.createElement("button", { "aria-label": M.HISTORY_BACK, className: 'portal-profile-detail-back', onClick: onBack, type: 'button' }, '‹'),
            React.createElement("span", { className: 'portal-profile-detail-title' }, M.AI_ASSISTANT_TITLE)),
        React.createElement("div", { className: 'portal-ai-chat-list', ref: listRef }, messages.map(item => (React.createElement("div", { className: `portal-ai-chat-bubble portal-ai-chat-bubble-${item.role}`
                + (item.pending ? ' portal-ai-chat-bubble-pending' : ''), key: item.id }, item.content || (item.pending ? M.AI_ASSISTANT_SENDING : ''))))),
        error ? (React.createElement("p", { className: 'portal-ai-chat-error', role: 'alert' }, error)) : null,
        React.createElement("form", { className: 'portal-ai-chat-composer', onSubmit: onSubmit },
            React.createElement("textarea", { "aria-label": M.AI_ASSISTANT_PLACEHOLDER, className: 'portal-ai-chat-input', disabled: loading, maxLength: AI_ASSISTANT_INPUT_MAX_LENGTH, onChange: event => setDraft(event.target.value), onKeyDown: event => {
                    if (event.key === 'Enter' && !event.shiftKey) {
                        event.preventDefault();
                        event.currentTarget.form?.requestSubmit();
                    }
                }, placeholder: M.AI_ASSISTANT_PLACEHOLDER, rows: 1, value: draft }),
            React.createElement("button", { className: 'portal-ai-chat-send', disabled: loading || !draft.trim(), type: 'submit' }, loading ? M.AI_ASSISTANT_SENDING : M.AI_ASSISTANT_SEND))));
}
